import { CommitteeRepository } from '@/store/committee-state/committee-state.repository';
import { Ac, Committee, CommitteeDto, RefTypeEnum, User } from '@/types';
import { Component, computed, effect, inject, OnInit } from '@angular/core';
import { CommitteeService } from '../committee.service';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { getFormBuilderObjectFactory } from '@/components/form-builder/utils/form-builder-factory';
import { DynamicForm, SearchableSelectOption, SelectOption } from '@/components/form-builder/form-builder.types';
import { getCommitteeFormObject } from '../utils/committee-form-object';
import { RefParamsService } from '../../ref-params/ref-params.service';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { ButtonModule } from 'primeng/button';
import { TabsModule } from 'primeng/tabs';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ProfileService } from '@/pages/profile/profile.service';
import { MembersComponent } from '../../members/members.component';
import { DialogService } from 'primeng/dynamicdialog';
import { ConfirmationService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { committeeSchema } from '@/validations/commitee.validation';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AcsComsComponent } from '../acs-coms/acs-coms.component';
@Component({
  selector: 'sm-edit-committee',
  imports: [
    PageHeaderComponent,
    FormBuilderComponent,
    ButtonModule,
    TranslateModule,
    TabsModule,
    CommonModule,
    MembersComponent,
    DialogModule,
    ConfirmDialogModule,
    AcsComsComponent,
  ],
  providers: [DialogService, ConfirmationService],
  templateUrl: './edit-committee.component.html',
  styleUrl: './edit-committee.component.scss',
})
export class EditCommitteeComponent implements OnInit {
  //services
  committeeRepository = inject(CommitteeRepository);
  committeeService = inject(CommitteeService);
  messageService = inject(MessageService);
  activatedRoute = inject(ActivatedRoute);
  translate = inject(TranslateService);
  refParamsService = inject(RefParamsService);
  profileService = inject(ProfileService);
  router = inject(Router);
  confirmationService = inject(ConfirmationService);

  private initialFormValues: any = null;
  hasFormChanges = false;
  existingDocs: any[] = []; // Track existing documents

  // Reactive observables so the template reflects store updates immediately
  users$ = this.committeeRepository.getNestedObservable<User[]>('response', 'users');
  conselliers$ = this.committeeRepository.getNestedObservable<User[]>('response', 'conselliers');
  committeeId$ = this.committeeRepository.getNestedObservable<number>('response', 'id');

  queryParamMapSignal = toSignal(this.activatedRoute.queryParamMap, {
    initialValue: new Map() as unknown as ParamMap,
  });

  isInspectMode = computed(() => {
    const paramMap = this.queryParamMapSignal();
    return paramMap.get('inspect') === 'true';
  });

  constructor() {
    effect(() => {
      this.committeeRepository.setNested('isDisabled', {
        nom: this.isInspectMode(),
        abreviation: this.isInspectMode(),
        nomAr: this.isInspectMode(),
        decretCreation: this.isInspectMode(),
        responsable: this.isInspectMode(),
        special: this.isInspectMode(),
        adresse: this.isInspectMode(),
        contact: this.isInspectMode(),
        ancrage: this.isInspectMode(),
        comTypeId: this.isInspectMode(),
        members: this.isInspectMode(),
        tutelId: this.isInspectMode(),
        theAdresse: this.isInspectMode(),
        files: this.isInspectMode(),
      });
    });
  }

  breadcrumb = [
    {
      label: 'settings.committee.breadcrumb.parametres',
      routerLink: ['/settings'],
    },
    {
      label: 'settings.committee.breadcrumb.committee',
      routerLink: ['/settings', 'committee'],
    },
    {
      label: this.isInspectMode() ? 'settings.committee.breadcrumb.consult' : 'settings.committee.breadcrumb.update',
      routerLink: ['/settings', 'committee', 'update'],
    },
  ];

  selectedTab = 'members';
  committeeTypesOptions: BehaviorSubject<SelectOption[]> = new BehaviorSubject<SelectOption[]>([]);
  tutelleOptions: BehaviorSubject<SearchableSelectOption[]> = new BehaviorSubject<SearchableSelectOption[]>([]);
  paysOptions: SelectOption[] = [];
  membersOptions: BehaviorSubject<SearchableSelectOption[]> = new BehaviorSubject<SearchableSelectOption[]>([]);
  formObject: DynamicForm = getFormBuilderObjectFactory();
  formDateLoading = false;
  isAcsComsVisible = false;
  loading = false;
  id = 0;

  // Form validation - reactive observable
  isFormValid$: Observable<boolean> = combineLatest([
    this.committeeRepository.getNestedObservable('dto', 'nom'),
    this.committeeRepository.getNestedObservable('dto', 'decretCreation'),
    this.committeeRepository.getNestedObservable('dto', 'comTypeId'),
    this.committeeRepository.getObservable('comTypeIdSelected'),
    this.committeeRepository.getNestedObservable('dto', 'responsableId'),
    this.committeeRepository.getNestedObservable<string>('dto', 'theAdresse', 'email'),
  ]).pipe(
    map(([nom, decretCreation, comTypeId, comTypeIdSelected, responsableId, email]) => {
      // Check all required fields
      const isEmailValid = email && typeof email === 'string' && email.trim() !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
      return !!(nom && decretCreation && comTypeId && comTypeIdSelected && responsableId && isEmailValid);
    })
  );
  ngOnInit(): void {
    this.formDateLoading = true;
    this.initRouting();
  }

  initRouting() {
    this.activatedRoute.fragment.subscribe(fragment => {
      if (fragment !== null && ['0', '1', '2'].includes(fragment)) {
        this.selectedTab = fragment;
      }
    });

    this.formDateLoading = true;
    this.id = Number(this.activatedRoute.snapshot.paramMap.get('id'));
    this.loadData();
  }

  updateCommittee() {
    const committe = this.committeeRepository.get<CommitteeDto>('dto');

    // Validate the data
    const result = committeeSchema.safeParse(committe);
    if (!result.success) {
      this.committeeRepository.setErrors(result.error.flatten().fieldErrors);
      return;
    }

    // Existing docs that should remain (filter out deleted ones)
    const filteredDocs = this.existingDocs.filter(existingDoc => {
      // Check if this existing doc is still in the current docs array
      return committe.docs?.some(currentDoc => currentDoc.id === existingDoc.id);
    });

    // Prepare the update data with correct files and docs
    const updateData = {
      ...committe,
      // New files to be added
      files: committe.files || [],
      // Existing docs that should remain
      docs: filteredDocs,
      // Doc IDs of the existing docs that should remain
      docIds: filteredDocs.map(doc => doc.id),
    };

    this.committeeService.updateCommittee(this.id, updateData).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.committee.message.success.edited'),
        });
        this.hasFormChanges = false;
        this.router.navigate(['/settings', 'committee']);
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message || this.translate.instant('settings.committee.message.error.edited'),
        });
      },
    });
  }

  onTabChange(event: any) {
    this.selectedTab = event;
    this.router.navigate([], {
      fragment: event,
      queryParamsHandling: 'preserve',
      replaceUrl: true,
    });
  }

  loadData() {
    this.committeeService.getOneCommittee(this.id).subscribe({
      next: response => {
        // Store existing docs for tracking
        this.existingDocs = (response as any).docs || [];
        this.committeeRepository.set('response', response);
        this.isAcsComsVisible = this.committeeRepository.get<Committee>('response').comType.code === 'CPMP' && this.isInspectMode();

        const dto: CommitteeDto = {
          nom: response.nom,
          abreviation: response.abreviation ?? '',
          nomAr: response.nomAr ?? '',
          ancrage: null,
          decretCreation: response.decretCreation,
          comTypeId: response.comType?.id,
          responsableId: response.responsableId,
          tutelId: (response as any).tutelId || null,
          theAdresse: {
            id: (response as any)?.theAdresse?.id || (response as any)?.adresseId,
            email: (response as any)?.theAdresse?.email || '',
            tel1: (response as any)?.theAdresse?.tel1 || '',
            tel2: (response as any)?.theAdresse?.tel2 || '',
            fax: (response as any)?.theAdresse?.fax || '',
            adresse: (response as any)?.theAdresse?.adresse || '',
            compAdresse: (response as any)?.theAdresse?.compAdresse || '',
            codePostal: (response as any)?.theAdresse?.codePostal || '',
            ville: (response as any)?.theAdresse?.ville || '',
            paysId: (response as any)?.theAdresse?.pays?.id || null,
          },
          contact: null,
          special: response.special,
          userIds: response.users.map(user => user.id),
          consellierIds: (response.conselliers || []).map(user => user.id as any),
          docs: (response as any).docs || [],
        };
        this.committeeRepository.set('memberSelected', { code: response.responsableId });
        this.committeeRepository.set('dto', dto);
        this.initialFormValues = { ...dto };

        // Set the selected tutel if it exists
        if ((response as any).tutel) {
          this.committeeRepository.set('tuttelSelected', {
            name: (response as any).tutel.nom,
            code: (response as any).tutel.id,
          });
        }

        // Set the selected pays if it exists
        if ((response as any)?.theAdresse?.pays) {
          this.committeeRepository.set('paysSelected', {
            name: (response as any).theAdresse.pays.description,
            code: (response as any).theAdresse.pays.id,
          });
        }

        this.committeeRepository.set('comTypeIdSelected', { name: response.comType?.description, code: response.comType?.id });
        this.committeeRepository.set('membersSelected', []);

        // Initialize previousComType to track changes
        // this.previousComType = { name: response.comType?.description, code: response.comType?.id };

        this.refParamsService.getSpecializedRefParams(RefTypeEnum.COMMITEE_TYPE).subscribe(comTypeOptions => {
          this.committeeTypesOptions.next(
            comTypeOptions.map(refType => {
              return { name: refType.description, code: refType.id };
            })
          );

          // Set the commission type options in the store
          this.committeeRepository.set('comTypeOptions', this.committeeTypesOptions.getValue());

          this.committeeService
            .getTutelsWithoutCom(
              this.committeeRepository.getNested('comTypeIdSelected', 'code'),
              this.committeeRepository.getNested('response', 'id')
            )
            .subscribe((tutelleOptions: Ac[]) => {
              this.tutelleOptions.next(
                tutelleOptions.map((ref: Ac) => ({
                  name: ref.nom,
                  code: ref.id,
                  searchable: `${ref.nom}_${ref.abreviation}_${ref.nomAr}`,
                }))
              );
              if (response?.tutel) {
                const current = { name: response.tutel.nom, code: response.tutel.id };
                if (!this.tutelleOptions.getValue().some(opt => opt.code === current.code)) {
                  this.tutelleOptions.next([current, ...this.tutelleOptions.getValue()]);
                }
              }
              this.refParamsService.getSpecializedRefParams(RefTypeEnum.COUNTRY).subscribe(paysOptions => {
                this.paysOptions = paysOptions.map((p: any) => ({ name: p.description, code: p.id }));
                this.profileService.getUserList().subscribe(membersOptions => {

                  // Subscribe to committee changes so the dropdown updates dynamically when a conseiller is added/deleted
                  this.committeeRepository.getObservable<Committee>('response').subscribe(currentResponse => {
                    if (!currentResponse) return;

                    const consellierIds = new Set(
                      (currentResponse.conselliers || []).map(u => String(u.id))
                    );

                    const uniqueAvailableUsers = membersOptions
                      // 1. Deduplicate the backend response securely by user ID
                      .filter((user, index, self) => index === self.findIndex(t => t.id === user.id))
                      // 2. Exclude users who are conselliers (EXCEPT the current president if they somehow are one)
                      .filter(user => !consellierIds.has(String(user.id)) || String(user.id) === String(currentResponse.responsableId))
                      // 3. Map the clean data to the dropdown UI format
                      .map(user => ({
                        name: `${user.firstName} ${user.lastName}`.trim(),
                        code: user.id,
                        searchable: `${user.username}_${user.firstName}_${user.lastName}_${user.email}`.trim(),
                      }));

                    this.membersOptions.next(uniqueAvailableUsers);
                    this.committeeRepository.set('memberOptions', this.membersOptions.getValue());
                  });

                  // Only create the form object if it hasn't been initialized yet
                  if (!this.formInitialized) {
                    this.formObject = getCommitteeFormObject({
                      store: this.committeeRepository,
                      comTypeOptions: this.committeeTypesOptions,
                      tutelleOptions: this.tutelleOptions,
                      paysOptions: this.paysOptions,
                      memberOptions: this.membersOptions,
                      isInspectMode: this.isInspectMode(),
                      downloadHandler: (alfrescoId: string, fileName?: string, fileExtension?: string) =>
                        this.downloadDocument(alfrescoId, fileName, fileExtension),
                      isDetailsMode: this.isInspectMode(),
                    });
                    this.formInitialized = true;

                    // Store initial form values to prevent any changes
                    this.initialFormValues = JSON.parse(JSON.stringify(this.committeeRepository.get('dto')));

                    // Store stable references to prevent form recreation
                    this.stableFormObject = this.formObject;
                  } else {
                    // Use the stable form object reference
                    this.formObject = this.stableFormObject!;
                  }
                });

                this.formDateLoading = false;
              });
            });
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.committee.message.error.loading'),
        });
      },
    });
  }

  checkFormChanges() {
    const currentValues = this.committeeRepository.get('dto');
    const hasOtherChanges = JSON.stringify(currentValues) !== JSON.stringify(this.initialFormValues);

    // Only set form changes if there are actual changes, not just commission type changes
    this.hasFormChanges = hasOtherChanges;
  }

  onCancel() {
    this.checkFormChanges();
    if (this.hasFormChanges) {
      this.confirmationService.confirm({
        header: this.translate.instant('settings.committee.update.confirmationDialog.title'),
        message: this.translate.instant('settings.committee.update.confirmationDialog.description'),
        acceptLabel: this.translate.instant('settings.committee.update.confirmationDialog.yes'),
        rejectLabel: this.translate.instant('settings.committee.update.confirmationDialog.no'),
        accept: () => {
          this.router.navigate(['/settings', 'committee']);
        },
      });
    } else {
      this.router.navigate(['/settings', 'committee']);
    }
  }

  private formInitialized = false;
  private stableFormObject: DynamicForm | null = null;

  onFormChange() {
    // Only check form changes, don't do anything else
    // This prevents any field refreshing when commission type changes
    this.checkFormChanges();
  }

  downloadDocument(alfrescoId: string, fileName?: string, fileExtension?: string) {
    this.committeeService.downloadDocument(alfrescoId).subscribe({
      next: (blob: Blob) => {
        // Create a download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        // Use the actual file extension if provided, otherwise default to pdf
        const extension = fileExtension || 'pdf';
        const name = fileName || `document_${alfrescoId}`;
        link.download = `${name}.${extension}`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.committee.message.error.download'),
        });
      },
    });
  }
}
